import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, Star, ShieldCheck, BadgeCheck, Leaf, MapPin } from "lucide-react";
import { MaskedLines, Reveal, RevealLine } from "@/components/Motion";
import ServiceGrid from "@/components/ServiceGrid";
import EditorialMarquee from "@/components/EditorialMarquee";
import Testimonials from "@/components/Testimonials";
import { CONTACT, IMAGES, TRUST, MANIFESTO, REVIEW_STATS } from "@/data/site";

const iconMap = { ShieldCheck, BadgeCheck, Leaf, MapPin };

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} data-testid="hero" className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-[#FAFAF9]">
        <div className="max-w-[1440px] w-full mx-auto px-5 sm:px-8 pt-36 pb-14 grid lg:grid-cols-12 gap-10 items-end relative z-10">
          <div className="lg:col-span-7">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="kicker mb-6">
              Adelaide · South Australia
            </motion.p>
            <h1 className="font-display font-extrabold tracking-tight text-[#1C2A39] text-5xl sm:text-7xl lg:text-[5.6rem] leading-[0.95]">
              <MaskedLines lines={["A brighter", "kind of"]} delay={0.15} />
              <span className="line-mask block">
                <motion.span initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.39 }} className="block text-[#0D9488]">
                  clean.
                </motion.span>
              </span>
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="mt-7 text-lg text-[#475569] max-w-lg leading-relaxed">
              Premium residential, commercial & end-of-lease cleaning across Adelaide. Police-checked, eco-friendly and backed by our 100% satisfaction guarantee.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }} className="mt-9 flex flex-wrap gap-4">
              <Link to="/quote" data-testid="hero-quote-btn" className="btn-primary">Get a Free Quote <ArrowRight size={18} /></Link>
              <a href={`tel:${CONTACT.phoneRaw}`} data-testid="hero-call-btn" className="btn-ghost"><Phone size={17} /> {CONTACT.phone}</a>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }} className="flex items-center gap-4 lg:justify-end">
              <div className="flex gap-0.5">
                {[0, 1, 2, 3].map((i) => <Star key={i} size={18} className="text-[#F59E0B]" fill="#F59E0B" />)}
                <span className="relative inline-block">
                  <Star size={18} className="text-[#F59E0B]" />
                  <span className="absolute inset-0 overflow-hidden" style={{ width: "70%" }}>
                    <Star size={18} className="text-[#F59E0B]" fill="#F59E0B" />
                  </span>
                </span>
              </div>
              <p className="text-sm text-[#1C2A39]"><span className="font-semibold">{REVIEW_STATS.average}</span> · {REVIEW_STATS.count} Google reviews</p>
            </motion.div>
          </div>
        </div>

        {/* Parallax hero image band */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 1.2 }} className="relative z-10 max-w-[1440px] w-full mx-auto px-5 sm:px-8">
          <div className="img-hover-wrap clip-arch h-[38vh] sm:h-[46vh] relative">
            <motion.img style={{ y: imgY, scale: imgScale }} src={IMAGES.heroLiving} alt="Freshly cleaned space by Britelines in Adelaide" className="w-full h-[130%] object-cover -mt-[15%]" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
          </div>
        </motion.div>
        <div className="h-14" />
      </section>

      {/* TRUST BAR */}
      <section data-testid="trust-bar" className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {TRUST.map((t, i) => {
            const Icon = iconMap[t.icon] || ShieldCheck;
            return (
              <Reveal key={t.label} delay={i * 0.08}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#E8F1EB] flex items-center justify-center shrink-0">
                    <Icon size={22} className="text-[#0D9488]" strokeWidth={1.7} />
                  </div>
                  <div>
                    <h3 className="font-display font-medium text-lg text-[#1C2A39]">{t.label}</h3>
                    <p className="text-sm text-[#475569] mt-1">{t.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <EditorialMarquee />

      {/* SERVICES */}
      <section data-testid="services-preview" className="py-24 sm:py-32 bg-[#FAFAF9]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div className="max-w-xl">
              <p className="kicker mb-4">What we do</p>
              <h2 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1C2A39] leading-[1.05]">
                <RevealLine>Cleaning services</RevealLine>
                <RevealLine delay={0.1}>for every space.</RevealLine>
              </h2>
            </div>
            <Reveal delay={0.2}><Link to="/services" className="btn-ghost">All services <ArrowRight size={16} /></Link></Reveal>
          </div>
          <ServiceGrid />
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section data-testid="manifesto" className="py-24 sm:py-32 bg-[#1C2A39] text-white relative overflow-hidden grain">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 relative z-10">
          <p className="kicker !text-[#5EEAD4] mb-16">Our standard</p>
          <div className="space-y-4">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.05}>
                <div className="grid md:grid-cols-12 gap-6 py-10 border-t border-white/10 items-start">
                  <span className="md:col-span-2 font-display font-light text-6xl sm:text-7xl text-[#5EEAD4] leading-none">{m.n}</span>
                  <h3 className="md:col-span-6 font-display font-medium text-3xl sm:text-4xl tracking-tight leading-[1.1]">{m.title}</h3>
                  <p className="md:col-span-4 text-slate-300 leading-relaxed md:pt-2">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARALLAX FEATURE */}
      <FeatureParallax />

      <Testimonials />

      {/* CTA */}
      <section data-testid="home-cta" className="py-24 sm:py-32 bg-[#E8F1EB]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display font-medium text-4xl sm:text-6xl lg:text-7xl tracking-tight text-[#1C2A39] leading-[1.02] max-w-4xl mx-auto">
              Ready for a spotless space?
            </h2>
            <p className="mt-6 text-lg text-[#475569] max-w-xl mx-auto">Get your free, no-obligation quote today. Fast response from Adelaide's trusted cleaning team.</p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/quote" className="btn-primary">Get a Free Quote <ArrowRight size={18} /></Link>
              <a href={`tel:${CONTACT.phoneRaw}`} className="btn-ghost"><Phone size={17} /> Call {CONTACT.phone}</a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function FeatureParallax() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section ref={ref} data-testid="feature-parallax" className="py-24 sm:py-32 bg-[#FAFAF9]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div className="img-hover-wrap clip-arch-sm h-[60vh] relative order-2 lg:order-1">
          <motion.img style={{ y }} src={IMAGES.ecoBottles} alt="Eco-friendly cleaning products" className="w-full h-[120%] object-cover -mt-[10%]" />
        </div>
        <div className="order-1 lg:order-2">
          <p className="kicker mb-4">Eco-friendly by choice</p>
          <h2 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1C2A39] leading-[1.05]">
            <RevealLine>Kind to your home.</RevealLine>
            <RevealLine delay={0.1}>Kinder to Adelaide.</RevealLine>
          </h2>
          <p className="mt-6 text-[#475569] leading-relaxed max-w-md">
            We use low-tox, environmentally responsible products that are safe for your family and pets — without ever compromising on that brilliant, streak-free finish.
          </p>
          <ul className="mt-8 space-y-3">
            {["Non-toxic, biodegradable products", "Safe around children & pets", "Micro-fibre & reduced-water methods"].map((p) => (
              <li key={p} className="flex items-center gap-3 text-[#1C2A39]"><Leaf size={18} className="text-[#0D9488]" /> {p}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
